/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { createSlice, injectReducers } from '@apia/store';
import {
  TResponsiveTableAction,
  TResponsiveTableIndexColumns,
  TResponsiveTableSlice,
  TResponsiveTableState,
} from './types';
import { getInitialState as selectionGetInitialState } from './selection/getInitialState';
import { setNewFocused } from './selection/setNewFocused';
import { TResponsiveTableRow } from '../types';
import { getReduxActions } from './selection/getReduxActions';
import { getController2ReduxActions } from '../controller2/getController2ReduxActions';

function getInitialState(): TResponsiveTableState {
  const newState: TResponsiveTableState = {
    ...selectionGetInitialState(),
    allowSelection: true,
    allowSorting: true,
    filters: [],
    isLoading: false,
    isMultiple: true,
    accordionIndexColumns: {} as TResponsiveTableIndexColumns,
    expandedRows: [],
  };

  return newState;
}

const responsiveTableSlice = createSlice({
  name: 'responsiveTableSlice',
  initialState: {} as TResponsiveTableSlice,
  reducers: {
    update(
      state,
      {
        payload: { tableName, handleFocus, ...rest },
      }: TResponsiveTableAction<
        Partial<TResponsiveTableState> & {
          isFocused?: boolean;
          handleFocus?: boolean;
        }
      >,
    ) {
      if (!state[tableName]) state[tableName] = getInitialState();
      state[tableName] = { ...state[tableName], ...rest };

      if (handleFocus !== false) {
        setNewFocused(state[tableName], {
          altKey: false,
          ctrlKey: false,
          shiftKey: false,
          shouldSelectNewFocused: rest.isFocused ?? state[tableName].isFocused,
        });
      }

      if (rest.columns) {
        state[tableName].nonAdditionalColumnsCount = rest.columns.filter(
          (current) => !current.showAsAdditional,
        ).length;
      }

      if (rest.filters) {
        state[tableName].hasNonAdditionalFilters =
          rest.filters.filter(
            (current) =>
              !current.asAdditional &&
              (rest.columns ?? state[tableName].columns)?.find(
                (search) => search.name === current.column,
              ),
          ).length > 0;
      }
    },
    addRows(
      state,
      { payload }: TResponsiveTableAction<{ newRows: TResponsiveTableRow[] }>,
    ) {
      if (!state[payload.tableName])
        state[payload.tableName] = getInitialState();
      state[payload.tableName].rows = state[payload.tableName].rows.concat(
        ...payload.newRows,
      );
      setNewFocused(state[payload.tableName], {
        altKey: false,
        ctrlKey: false,
        shiftKey: false,
        shouldSelectNewFocused: true,
        newFocusedRow: state[payload.tableName].rows.length,
      });
    },
    deleteRows(
      state,
      { payload }: TResponsiveTableAction<{ rowIndices: number[] }>,
    ) {
      const table = state[payload.tableName];
      const minIndex = payload.rowIndices.reduce((min, current) => {
        return current < min ? current : min;
      }, Infinity);
      table.rows = table.rows.filter(
        (_, index) => !payload.rowIndices.includes(index),
      );

      setNewFocused(state[payload.tableName], {
        altKey: false,
        ctrlKey: false,
        shiftKey: false,
        shouldSelectNewFocused: true,
        newFocusedRow: minIndex,
      });
      state[payload.tableName].isFocused = false;
    },
    destroy(state, { payload: { tableName } }: TResponsiveTableAction) {
      delete state[tableName];
    },
    swapFocusedRow(
      state,
      {
        payload: { newIndex, tableName },
      }: TResponsiveTableAction<{ newIndex: number }>,
    ) {
      const tableState = state[tableName];
      const focusedIndex = tableState.focusedRow;

      if (
        focusedIndex === undefined ||
        newIndex < 0 ||
        newIndex >= tableState.rows.length
      )
        return;

      if (focusedIndex === newIndex) {
        if (tableState.draggingRow === newIndex)
          tableState.draggingRow = undefined;
        else tableState.draggingRow = newIndex;
        return;
      }

      const isSelected = tableState.selectedRows.includes(focusedIndex);

      if (isSelected) {
        tableState.selectedRows = [
          ...tableState.selectedRows.filter(
            (current) => current !== focusedIndex,
          ),
          newIndex,
        ];
      }

      const aux = tableState.rows[newIndex];
      tableState.rows[newIndex] = tableState.rows[focusedIndex];
      tableState.rows[focusedIndex] = aux;

      tableState.focusedRow = newIndex;
      tableState.draggingRow = newIndex;
    },

    // Rows selection
    ...getReduxActions(),
    ...getController2ReduxActions(),
  },
});

type TypedUseSelectorHook<TState> = <TSelected>(
  selector: (state: TState) => TSelected,
  equalityFn?: (a: TSelected, b: TSelected) => boolean,
) => TSelected;
const { store: responsiveTableStore, useSelector } = injectReducers({
  responsiveTableSlice: responsiveTableSlice.reducer,
});

const useResponsiveTable = useSelector as TypedUseSelectorHook<{
  responsiveTableSlice: TResponsiveTableSlice;
}>;
export { responsiveTableStore };
export { useResponsiveTable };

export const responsiveTableActions = responsiveTableSlice.actions;
